import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { inventoryErrorKey } from '@/features/inventory/inventoryErrors';
import { INPUT, SECONDARY_BUTTON, formatInstant } from '@/features/inventory/registerUi';
import { CheckRow } from '@/features/provisioning/GoLivePanel';
import type { CheckResult } from '@/features/provisioning/useProvisioning';
import {
  failingChecksFrom,
  useConfirmLive,
  useCreateInstallation,
  useUpdateInstallation,
  useVehicleInstallation,
  type Installation,
  type InstallationWriteBody,
  type SystemVoltage,
} from './useInstallations';

/**
 * THE INSTALL RECORD (slice 30), rendered DIRECTLY ABOVE `GoLivePanel` at all
 * three entrances — the wizard's step 4, the `/vehicles` row detail, and the
 * `/live` selection. One component owns every state so a record cannot look
 * different depending on which screen opened it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 THE STATES, AND WHY EACH LOOKS THE WAY IT DOES.
 * ─────────────────────────────────────────────────────────────────────────────
 *   no tracker      one line, no form — there is no assignment to annotate yet.
 *   no record       the form → Save.
 *   unconfirmed     the record + Edit + "Confirm live", which runs the device
 *                   checks server-side and shows the FAILING ones inline (the
 *                   same `CheckRow` the go-live panel uses) on a 409.
 *   confirmed       who/when + Edit (descriptive fields stay editable).
 *   closed          read-only — the assignment was closed after the record was
 *                   written, and history is not rewritten.
 *
 * 🔴 CONFIRM-LIVE IS A SERVER RESULT, NEVER A CHECKBOX. There is no "confirmed"
 * control the operator can tick — `verified_live_at` is stamped only when the
 * server's own checks pass, and the panel renders whatever the server returns.
 *
 * 🔴 NO PHOTO, NO STOCK, NO JOB-SCHEDULING CONTROL ANYWHERE — not disabled, not
 * "coming soon". Mahad, 14 Sep: v1 is the record only, text fields only. Absence
 * over disabled.
 *
 * 🔴 INDIGO ADMIN TOKENS ONLY (`raad-primary` / `raad-accent`), never green.
 */

const VOLTAGES: readonly SystemVoltage[] = [12, 24];

interface FormState {
  installerName: string;
  installerPhone: string;
  systemVoltage: SystemVoltage;
  placement: string;
  relayFitted: boolean;
  flybackDiodeFitted: boolean;
  inlineFuseFitted: boolean;
  tamperSealId: string;
  notes: string;
}

const EMPTY_FORM: FormState = {
  installerName: '',
  installerPhone: '',
  systemVoltage: 12,
  placement: '',
  relayFitted: false,
  flybackDiodeFitted: false,
  inlineFuseFitted: false,
  tamperSealId: '',
  notes: '',
};

function formFromRecord(r: Installation): FormState {
  return {
    installerName: r.installerName,
    installerPhone: r.installerPhone ?? '',
    systemVoltage: r.systemVoltage,
    placement: r.placement,
    relayFitted: r.relayFitted,
    flybackDiodeFitted: r.flybackDiodeFitted ?? false,
    inlineFuseFitted: r.inlineFuseFitted ?? false,
    tamperSealId: r.tamperSealId ?? '',
    notes: r.notes ?? '',
  };
}

/**
 * Build the wire body from the form. The relay pairing is enforced BY
 * CONSTRUCTION: the diode/fuse flags are sent ONLY when a relay is fitted, and
 * omitted otherwise, matching the server's forbidden-otherwise CHECK. On EDIT an
 * empty optional text field clears it (`null`); on CREATE it is omitted (the
 * create schema does not accept `null`).
 */
function toBody(f: FormState, mode: 'create' | 'edit'): InstallationWriteBody {
  const optional = (v: string): string | null | undefined => {
    const t = v.trim();
    if (t) return t;
    return mode === 'edit' ? null : undefined;
  };
  const body: InstallationWriteBody = {
    installerName: f.installerName.trim(),
    systemVoltage: f.systemVoltage,
    placement: f.placement.trim(),
    relayFitted: f.relayFitted,
  };
  const phone = optional(f.installerPhone);
  if (phone !== undefined) body.installerPhone = phone;
  if (f.relayFitted) {
    body.flybackDiodeFitted = f.flybackDiodeFitted;
    body.inlineFuseFitted = f.inlineFuseFitted;
  }
  const seal = optional(f.tamperSealId);
  if (seal !== undefined) body.tamperSealId = seal;
  const notes = optional(f.notes);
  if (notes !== undefined) body.notes = notes;
  return body;
}

/** A labelled text/checkbox field, in the register's field furniture. */
function Labelled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[12px] font-medium text-raad-muted">{label}</span>
      {children}
    </label>
  );
}

function yesNo(t: (k: string) => string, v: boolean | null): string {
  if (v === null) return t('installations.field.na');
  return v ? t('installations.field.yes') : t('installations.field.no');
}

function InstallationForm({
  initial,
  mode,
  pending,
  errorKey,
  onSubmit,
  onCancel,
}: {
  initial: FormState;
  mode: 'create' | 'edit';
  pending: boolean;
  errorKey: string | null;
  onSubmit: (body: InstallationWriteBody) => void;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const [f, setF] = useState<FormState>(initial);
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setF((prev) => ({ ...prev, [k]: v }));

  const canSave = f.installerName.trim().length > 0 && f.placement.trim().length > 0;

  return (
    <form
      className="grid gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSave) onSubmit(toBody(f, mode));
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Labelled label={t('installations.field.installerName')}>
          <input
            className={INPUT}
            value={f.installerName}
            onChange={(e) => set('installerName', e.target.value)}
            autoFocus
          />
        </Labelled>
        <Labelled label={t('installations.field.installerPhone')}>
          <input
            className={INPUT}
            value={f.installerPhone}
            onChange={(e) => set('installerPhone', e.target.value)}
            inputMode="tel"
          />
        </Labelled>
        <Labelled label={t('installations.field.systemVoltage')}>
          <select
            className={INPUT}
            value={f.systemVoltage}
            onChange={(e) => set('systemVoltage', Number(e.target.value) as SystemVoltage)}
          >
            {VOLTAGES.map((v) => (
              <option key={v} value={v}>
                {t('installations.field.voltageOption', { v })}
              </option>
            ))}
          </select>
        </Labelled>
        <Labelled label={t('installations.field.placement')}>
          <input
            className={INPUT}
            value={f.placement}
            onChange={(e) => set('placement', e.target.value)}
          />
        </Labelled>
      </div>

      <label className="flex items-center gap-2 text-[13px]">
        <input
          type="checkbox"
          className="size-4 accent-raad-primary"
          checked={f.relayFitted}
          onChange={(e) => set('relayFitted', e.target.checked)}
        />
        {t('installations.field.relayFitted')}
      </label>

      {/* 🔴 THE DIODE/FUSE FLAGS APPEAR ONLY WHEN A RELAY IS FITTED. The server
          REQUIRES them then and FORBIDS them otherwise; showing them
          unconditionally would invite a body the DB CHECK refuses. */}
      {f.relayFitted ? (
        <div className="grid gap-2 rounded-lg border border-raad-border p-3 sm:grid-cols-2">
          <label className="flex items-center gap-2 text-[13px]">
            <input
              type="checkbox"
              className="size-4 accent-raad-primary"
              checked={f.flybackDiodeFitted}
              onChange={(e) => set('flybackDiodeFitted', e.target.checked)}
            />
            {t('installations.field.flybackDiodeFitted')}
          </label>
          <label className="flex items-center gap-2 text-[13px]">
            <input
              type="checkbox"
              className="size-4 accent-raad-primary"
              checked={f.inlineFuseFitted}
              onChange={(e) => set('inlineFuseFitted', e.target.checked)}
            />
            {t('installations.field.inlineFuseFitted')}
          </label>
        </div>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2">
        <Labelled label={t('installations.field.tamperSealId')}>
          <input
            className={INPUT}
            value={f.tamperSealId}
            onChange={(e) => set('tamperSealId', e.target.value)}
          />
        </Labelled>
        <Labelled label={t('installations.field.notes')}>
          <input
            className={INPUT}
            value={f.notes}
            onChange={(e) => set('notes', e.target.value)}
          />
        </Labelled>
      </div>

      {errorKey !== null ? (
        <p
          role="alert"
          className="rounded-lg border border-raad-error/40 bg-raad-error/10 p-3 text-[12.5px] text-raad-text"
        >
          {t(errorKey)}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <button
          type="submit"
          disabled={!canSave || pending}
          className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-raad-onprimary disabled:opacity-40"
        >
          {pending
            ? t('installations.saving')
            : mode === 'create'
              ? t('installations.save')
              : t('installations.saveChanges')}
        </button>
        {onCancel ? (
          <button type="button" className={SECONDARY_BUTTON} onClick={onCancel}>
            {t('installations.cancel')}
          </button>
        ) : null}
      </div>
    </form>
  );
}

/** A read-only field row in the record summary. */
function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-wrap gap-x-2 text-[12.5px]">
      <span className="text-raad-muted">{label}:</span>
      <span>{value}</span>
    </div>
  );
}

function RecordSummary({ r }: { r: Installation }) {
  const { t, i18n } = useTranslation();
  return (
    <div className="grid gap-1.5">
      <SummaryRow
        label={t('installations.field.installerName')}
        value={r.installerPhone ? `${r.installerName} · ${r.installerPhone}` : r.installerName}
      />
      <SummaryRow
        label={t('installations.field.systemVoltage')}
        value={t('installations.field.voltageOption', { v: r.systemVoltage })}
      />
      <SummaryRow label={t('installations.field.placement')} value={r.placement} />
      <SummaryRow
        label={t('installations.field.relayFitted')}
        value={
          r.relayFitted
            ? t('installations.summary.relayWith', {
                diode: yesNo(t, r.flybackDiodeFitted),
                fuse: yesNo(t, r.inlineFuseFitted),
              })
            : t('installations.field.no')
        }
      />
      {r.tamperSealId ? (
        <SummaryRow label={t('installations.field.tamperSealId')} value={r.tamperSealId} />
      ) : null}
      {r.notes ? <SummaryRow label={t('installations.field.notes')} value={r.notes} /> : null}
      <SummaryRow
        label={t('installations.field.installedAt')}
        value={formatInstant(r.installedAt, i18n.language)}
      />
    </div>
  );
}

export function InstallationPanel({ vehicleId }: { vehicleId: string }) {
  const { t, i18n } = useTranslation();
  const q = useVehicleInstallation(vehicleId);
  const create = useCreateInstallation(vehicleId);
  const update = useUpdateInstallation(vehicleId);
  const confirm = useConfirmLive(vehicleId);

  const [editing, setEditing] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [failing, setFailing] = useState<CheckResult[] | null>(null);

  const heading = <h3 className="mb-2 text-[13px] font-semibold">{t('installations.heading')}</h3>;
  const shell = (body: React.ReactNode) => (
    <div>
      {heading}
      {body}
    </div>
  );

  if (q.isPending) {
    return shell(<p className="text-[12.5px] text-raad-muted">{t('installations.loading')}</p>);
  }
  if (q.isError || q.data === undefined) {
    return shell(
      <p role="alert" className="text-[12.5px] text-raad-error">
        {t('installations.error')}
      </p>,
    );
  }

  // Defensive (slice 24b): coalesce a missing field rather than trusting the
  // shape — an unexpected body must not fall through to the "record exists"
  // branch and dereference a null.
  const installation = q.data.installation ?? null;
  const reason = q.data.reason ?? null;

  // ── No tracker: nothing to annotate. One line, no form. ──────────────────
  if (installation === null && reason === 'no_tracker') {
    return shell(<p className="text-[12.5px] text-raad-muted">{t('installations.noTracker')}</p>);
  }

  // ── No record: the create form. ──────────────────────────────────────────
  if (installation === null) {
    return shell(
      <div className="grid gap-3">
        <p className="text-[12.5px] text-raad-muted">{t('installations.noRecord')}</p>
        <InstallationForm
          initial={EMPTY_FORM}
          mode="create"
          pending={create.isPending}
          errorKey={errorKey}
          onSubmit={async (body) => {
            setErrorKey(null);
            try {
              await create.mutateAsync(body);
            } catch (e) {
              setErrorKey(inventoryErrorKey(e));
            }
          }}
        />
      </div>,
    );
  }

  // ── A record exists. ─────────────────────────────────────────────────────
  const closed = installation.assignmentClosed;
  const confirmed = installation.verifiedLiveAt !== null;

  if (editing && !closed) {
    return shell(
      <InstallationForm
        key={installation.updatedAt}
        initial={formFromRecord(installation)}
        mode="edit"
        pending={update.isPending}
        errorKey={errorKey}
        onCancel={() => {
          setEditing(false);
          setErrorKey(null);
        }}
        onSubmit={async (body) => {
          setErrorKey(null);
          try {
            await update.mutateAsync({ id: installation.id, body });
            setEditing(false);
          } catch (e) {
            setErrorKey(inventoryErrorKey(e));
          }
        }}
      />,
    );
  }

  async function pressConfirm() {
    setErrorKey(null);
    setFailing(null);
    try {
      await confirm.mutateAsync(installation!.id);
      // Success (or already-confirmed) refetches via invalidation and the panel
      // re-renders into the confirmed state. Nothing to set here.
    } catch (e) {
      const checks = failingChecksFrom(e);
      if (checks !== null) {
        setFailing(checks);
        setErrorKey('installations.confirmRefused');
      } else {
        setErrorKey(inventoryErrorKey(e));
      }
    }
  }

  return shell(
    <div className="grid gap-3">
      {closed ? (
        <p className="rounded-lg border border-raad-warn/40 bg-raad-warn/10 p-3 text-[12.5px]">
          {t('installations.closedBanner')}
        </p>
      ) : null}

      {confirmed ? (
        <p className="rounded-lg border border-raad-primary/40 bg-raad-primary/10 p-3 text-[12.5px]">
          {t('installations.confirmedOn', {
            when: formatInstant(installation.verifiedLiveAt as string, i18n.language),
          })}
        </p>
      ) : (
        <p className="text-[12.5px] text-raad-muted">{t('installations.unconfirmed')}</p>
      )}

      <RecordSummary r={installation} />

      {failing !== null ? (
        <ul className="rounded-xl border border-raad-border px-3">
          {failing.map((c) => (
            <CheckRow key={c.id} check={c} />
          ))}
        </ul>
      ) : null}

      {errorKey !== null ? (
        <p
          role="alert"
          className="rounded-lg border border-raad-error/40 bg-raad-error/10 p-3 text-[12.5px] text-raad-text"
        >
          {t(errorKey)}
        </p>
      ) : null}

      {/* A closed assignment is read-only: no Edit, no Confirm. */}
      {!closed ? (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={SECONDARY_BUTTON}
            onClick={() => {
              setErrorKey(null);
              setFailing(null);
              setEditing(true);
            }}
          >
            {t('installations.edit')}
          </button>
          {!confirmed ? (
            <button
              type="button"
              onClick={() => void pressConfirm()}
              disabled={confirm.isPending}
              className="min-h-9 rounded-lg bg-raad-primary px-3 text-[12.5px] font-semibold text-raad-onprimary disabled:opacity-40"
            >
              {confirm.isPending ? t('installations.confirming') : t('installations.confirmLive')}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>,
  );
}
